import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, today } from "@/lib/constants";

const EMPTY = { company_id: "", date: today(), time: "09:00", patient_name: "", patient_phone: "", pickup_address: "", dropoff_address: "", type: "sanitaire", notes: "" };

export default function Sorties() {
  const [rides, setRides] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/etablissement/rides").then((r) => setRides(r.data));
  useEffect(() => {
    load();
    api.get("/tribu").then((r) => {
      setMembers(r.data.members || []);
      if (r.data.members?.length) setForm((f) => ({ ...f, company_id: r.data.members[0].id, pickup_address: r.data.name?.replace("Tribu ", "") || "" }));
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_id) { toast.error("Sélectionnez un transporteur de votre Tribu"); return; }
    try {
      await api.post("/etablissement/rides", form);
      toast.success("Sortie transmise au transporteur");
      setOpen(false);
      setForm({ ...EMPTY, company_id: form.company_id });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const companyName = (id) => members.find((m) => m.id === id)?.company_name || "—";

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-primary" /> Saisie Express des Sorties
          </h1>
          <p className="text-muted-foreground text-sm">Transmettez vos sorties directement à un transporteur de votre Tribu.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="sortie-add-button"><Plus className="h-4 w-4" /> Nouvelle sortie</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nouvelle sortie</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Transporteur (Tribu)</Label>
                <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                  <SelectTrigger className="mt-1" data-testid="sortie-company"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1" /></div>
              <div><Label>Heure</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1" /></div>
              <div><Label>Patient</Label><Input value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} className="mt-1" data-testid="sortie-patient" /></div>
              <div><Label>Téléphone</Label><Input value={form.patient_phone} onChange={(e) => set("patient_phone", e.target.value)} className="mt-1" /></div>
              <div className="col-span-2"><Label>Adresse de départ</Label><Input value={form.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} className="mt-1" data-testid="sortie-pickup" /></div>
              <div className="col-span-2"><Label>Destination</Label><Input value={form.dropoff_address} onChange={(e) => set("dropoff_address", e.target.value)} className="mt-1" data-testid="sortie-dropoff" /></div>
              <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} data-testid="sortie-submit">Transmettre</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {members.length === 0 && (
        <Card className="p-4 rounded-md shadow-none border-accent bg-accent/10 text-sm">
          Ajoutez d'abord un transporteur dans <strong>Ma Tribu</strong> pour pouvoir transmettre des sorties.
        </Card>
      )}

      <Card className="p-0 rounded-md shadow-none overflow-hidden" data-testid="sorties-table">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Heure</TableHead><TableHead>Patient</TableHead><TableHead>Trajet</TableHead><TableHead>Transporteur</TableHead><TableHead>Statut</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rides.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Aucune sortie transmise.</TableCell></TableRow>}
            {rides.map((r) => (
              <TableRow key={r.id} data-testid={`sortie-row-${r.id}`}>
                <TableCell className="text-sm">{new Date(r.date).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="font-medium">{r.time}</TableCell>
                <TableCell>{r.patient_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate">{r.pickup_address} → {r.dropoff_address}</TableCell>
                <TableCell className="text-sm">{companyName(r.entreprise_id)}</TableCell>
                <TableCell>
                  {r.assigned_vehicle_id ? <Badge className={STATUS_META[r.status]?.cls}>{STATUS_META[r.status]?.label}</Badge> : <Badge variant="outline">En attente</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
